import { useRef, useLayoutEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import HeroScene from '../HeroScene';
import MagneticButton from '../MagneticButton';

gsap.registerPlugin(ScrollTrigger);

const HEADLINE = ['Come', 'home', 'to', 'yourself.'];

const Hero = () => {
  const root = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.hero-word',
        { y: 90, opacity: 0, filter: 'blur(14px)' },
        { y: 0, opacity: 1, filter: 'blur(0px)', duration: 2.2, stagger: 0.55, ease: 'power2.out', delay: 0.6 }
      );
      gsap.fromTo('.hero-sub', { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 2, delay: 3, ease: 'power2.out' });
      gsap.fromTo('.hero-cta', { opacity: 0 }, { opacity: 1, duration: 1.8, delay: 3.6, ease: 'power1.out' });

      ScrollTrigger.create({
        trigger: root.current,
        start: 'top top',
        end: '+=90%',
        pin: true,
        pinSpacing: true,
        scrub: 1.2,
        animation: gsap.timeline()
          .to('.hero-canvas', { scale: 1.18, ease: 'none' }, 0)
          .to('.hero-content', { y: -160, opacity: 0, ease: 'none' }, 0)
          .to('.hero-trees', { y: 40, scale: 1.08, ease: 'none' }, 0),
      });
    }, root);
    return () => ctx.revert();
  }, []);

  const scrollToContact = () => {
    const el = document.getElementById('contact');
    if (el && window.__lenis) window.__lenis.scrollTo(el, { duration: 3 });
  };

  return (
    <section id="hero" ref={root} data-testid="hero-section" className="relative h-[100svh] overflow-hidden">
      <div className="hero-canvas absolute inset-0 will-change-transform">
        <HeroScene />
      </div>
      <div
        className="hero-trees absolute bottom-0 left-0 right-0 h-[42%] pointer-events-none will-change-transform"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&w=1800&q=70)',
          backgroundSize: 'cover',
          backgroundPosition: 'center bottom',
          maskImage: 'linear-gradient(to top, rgba(0,0,0,0.55), transparent)',
          WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,0.55), transparent)',
          opacity: 0.5,
        }}
      />
      <div className="hero-content relative z-10 h-full flex flex-col items-center justify-center px-6 text-center will-change-transform">
        <p
          className="hero-sub text-[11px] md:text-xs uppercase tracking-[0.45em] text-[#F5F0E8]/95 mb-10 md:mb-14 opacity-0"
          style={{
            background: 'linear-gradient(180deg, rgba(16, 34, 28, 0.14), rgba(16, 34, 28, 0.08))',
            boxShadow: '0 10px 32px rgba(16, 34, 28, 0.08)',
            textShadow: '0 1px 12px rgba(13, 28, 24, 0.22)',
            borderRadius: '999px',
            padding: '0.28rem 0.9rem',
            backdropFilter: 'blur(2px)',
            WebkitBackdropFilter: 'blur(2px)',
          }}
        >
          Rise With Presence · Coaching for Women’s Wellbeing
        </p>
        <h1 className="font-serif-display font-light text-[#FAFAF8] text-5xl sm:text-7xl lg:text-[7.5vw] leading-[1.05] tracking-tight max-w-6xl" style={{ textShadow: '0 4px 60px rgba(26,77,56,0.5)' }}>
          {HEADLINE.map((w, i) => (
            <span key={i} className="hero-word inline-block mx-[0.14em] will-change-transform" style={{ opacity: 0 }}>
              {i === 3 ? <em className="font-light">{w}</em> : w}
            </span>
          ))}
        </h1>
        <div className="hero-cta mt-14 md:mt-20 opacity-0">
          <MagneticButton
            testId="hero-cta-button"
            onClick={scrollToContact}
            className="px-10 py-4 rounded-full border border-[#F5F0E8]/60 text-[#FAFAF8] text-xs uppercase tracking-[0.3em] backdrop-blur-sm bg-[#1A4D38]/20 hover:bg-[#1A4D38]/40 transition-colors duration-700"
          >
            Begin Your Journey
          </MagneticButton>
        </div>
      </div>
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3 text-[#F5F0E8]/60">
        <span className="text-[10px] uppercase tracking-[0.35em]">Breathe · Scroll slowly</span>
        <div className="w-px h-12 bg-gradient-to-b from-[#F5F0E8]/60 to-transparent" />
      </div>
    </section>
  );
};

export default Hero;
